import { toast } from "sonner";

const ADMIN_API_BASE = `${import.meta.env.VITE_API_BASE_URL}/api/admin`;

export interface AiConfig {
  id: string;
  monthlyBudgetUsd: number;
  perRequestBudgetUsd: number;
  maxTurnsOrchestrator: number;
  maxTurnsFormdata: number;
  maxTurnsSilae: number;
  maxTurnsValidation: number;
  modelOrchestrator: string;
  modelFormdata: string;
  modelSilaeScript: string;
  modelValidation: string;
  effortOrchestrator: string;
  effortFormdata: string;
  effortSilaeScript: string;
  effortValidation: string;
  globalDirectives: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

// Niveaux d'effort acceptés par le SDK Claude Agent (miroir de
// ALLOWED_EFFORT_LEVELS côté api-postgres).
export const EFFORT_LEVELS = ["low", "medium", "high", "xhigh", "max"] as const;

export interface UpdateAiConfigPayload {
  monthlyBudgetUsd?: number;
  perRequestBudgetUsd?: number;
  maxTurnsOrchestrator?: number;
  maxTurnsFormdata?: number;
  maxTurnsSilae?: number;
  maxTurnsValidation?: number;
  modelOrchestrator?: string;
  modelFormdata?: string;
  modelSilaeScript?: string;
  modelValidation?: string;
  effortOrchestrator?: string;
  effortFormdata?: string;
  effortSilaeScript?: string;
  effortValidation?: string;
  globalDirectives?: string | null;
}

export interface SelectableModel {
  id: string;
  inputPrice: number | null;
  outputPrice: number | null;
}

export interface AiConfigWithModels {
  config: AiConfig;
  allowedModels: SelectableModel[];
}

async function adminFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const response = await fetch(url, {
    credentials: "include",
    ...options,
    headers: { "Content-Type": "application/json", ...options.headers },
  });
  if (response.status === 401) {
    toast.error("Session administrateur expirée. Redirection en cours...");
    setTimeout(() => {
      window.location.href = "/admin";
    }, 1000);
    throw new Error("Session expirée");
  }
  return response;
}

export const aiConfigService = {
  getConfig: async (): Promise<AiConfigWithModels> => {
    const res = await adminFetch(`${ADMIN_API_BASE}/ai-config`, { method: "GET" });
    if (!res.ok) throw new Error("Échec du chargement de la configuration IA");
    const body = (await res.json()) as {
      data: AiConfig;
      allowedModels?: SelectableModel[];
    };
    return { config: body.data, allowedModels: body.allowedModels ?? [] };
  },

  updateConfig: async (payload: UpdateAiConfigPayload): Promise<AiConfig> => {
    const res = await adminFetch(`${ADMIN_API_BASE}/ai-config`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    const body = await res.json();
    if (!res.ok) {
      throw new Error(body?.message ?? "Échec de la mise à jour");
    }
    return (body as { data: AiConfig }).data;
  },
};
